/** Verified API-health presentation. No state is simulated. */
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Divider,
	Stack,
	Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useCallback, useEffect, useState } from "react";

import { GlassSurface } from "../../../layouts/components/GlassSurface";
import type { ApiClientMode } from "../../../services/apiClientProvider";
import { reducedMotionMediaQuery } from "../../../theme/motionTokens";

export interface SystemStatusPanelProps {
	apiMode: ApiClientMode;
}

type HealthStatus = "checking" | "healthy" | "unavailable" | "unknown";

interface HealthState {
	status: HealthStatus;
	detail: string;
}

function getApiBaseUrl(): string {
	const fromEnv = import.meta.env.VITE_API_BASE_URL;
	if (typeof fromEnv === "string" && fromEnv.length > 0) {
		return fromEnv.replace(/\/+$/, "");
	}
	return "http://localhost:3002/api/v2";
}

function getHealthUrl(): string {
	return `${getApiBaseUrl().replace(/\/api\/v2$/, "")}/health`;
}

export function SystemStatusPanel({ apiMode }: SystemStatusPanelProps) {
	const [attempt, setAttempt] = useState(0);
	const [health, setHealth] = useState<HealthState>(
		apiMode === "real"
			? { status: "checking", detail: "Checking the configured health endpoint…" }
			: {
					status: "unknown",
					detail: "Health is not checked while the explicit demonstration client is active.",
				}
	);

	const retry = useCallback(() => setAttempt((value) => value + 1), []);

	useEffect(() => {
		if (apiMode !== "real") {
			setHealth({
				status: "unknown",
				detail: "Health is not checked while the explicit demonstration client is active.",
			});
			return;
		}

		let cancelled = false;
		setHealth({ status: "checking", detail: "Checking the configured health endpoint…" });
		void fetch(getHealthUrl(), { method: "GET" })
			.then(async (response) => {
				if (cancelled) return;
				if (!response.ok) {
					setHealth({ status: "unavailable", detail: `Health endpoint returned HTTP ${response.status}.` });
					return;
				}
				const body = (await response.json().catch(() => null)) as
					| { service?: string }
					| null;
				setHealth({
					status: "healthy",
					detail: body?.service
						? `${body.service} responded successfully.`
						: "The configured health endpoint responded successfully.",
				});
			})
			.catch(() => {
				if (!cancelled) {
					setHealth({
						status: "unavailable",
						detail: "The configured health endpoint could not be reached.",
					});
				}
			});

		return () => {
			cancelled = true;
		};
	}, [apiMode, attempt]);

	return (
		<GlassSurface
			component="section"
			aria-labelledby="system-status-title"
			intensity="standard"
			depth="raised"
			sx={{ minWidth: 0 }}
		>
			<Box sx={{ p: 2.5 }}>
				<Typography id="system-status-title" component="h2" variant="h6">
					API health
				</Typography>
				<Typography variant="caption" sx={{ color: "workspace.textMuted" }}>
					Direct result from the configured runtime health mechanism
				</Typography>
			</Box>
			<Divider sx={{ borderColor: "workspace.border" }} />
			<Stack spacing={2} sx={{ p: 2.5 }} aria-live="polite" aria-busy={health.status === "checking"}>
				<Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
					<Typography variant="body2" sx={{ color: "workspace.textSecondary" }}>
						Client mode
					</Typography>
					<Chip label={apiMode === "real" ? "Live backend" : "Demonstration client"} size="small" />
				</Stack>
				<Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
					<Typography variant="body2" sx={{ color: "workspace.textSecondary" }}>
						Verified state
					</Typography>
					<HealthChip status={health.status} />
				</Stack>
				<Typography variant="body2" sx={{ color: "workspace.textSecondary" }}>
					{health.detail}
				</Typography>
				<Typography variant="caption" sx={{ color: "workspace.textMuted", overflowWrap: "anywhere" }}>
					Health source: {apiMode === "real" ? getHealthUrl() : "Not queried"}
				</Typography>
				{apiMode === "real" && health.status === "unavailable" && (
					<Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={retry} sx={{ alignSelf: "flex-start", color: "workspace.textPrimary", borderColor: "workspace.borderStrong" }}>
						Retry health check
					</Button>
				)}
			</Stack>
		</GlassSurface>
	);
}

function HealthChip({ status }: { status: HealthStatus }) {
	if (status === "checking") {
		return (
			<Chip
				icon={
					<CircularProgress
						size={14}
						aria-hidden="true"
						sx={{ [reducedMotionMediaQuery]: { animation: "none" } }}
					/>
				}
				label="Checking"
				size="small"
			/>
		);
	}
	if (status === "healthy") {
		return <Chip icon={<CheckCircleIcon />} label="Healthy" color="success" size="small" />;
	}
	if (status === "unavailable") {
		return <Chip icon={<ErrorOutlineIcon />} label="Unavailable" color="error" size="small" />;
	}
	return <Chip icon={<HelpOutlineIcon />} label="Unknown" size="small" />;
}
